/**
 * Issue #134 — "grounds near me", and the privacy design that is the whole
 * point of the ticket.
 *
 * The rule: **the matching happens on the device.** The browser hands the
 * position to the page, the page ranks the directory it already downloaded,
 * and nothing leaves the device. No request carries a player's live
 * coordinates, nothing is logged, nothing is stored.
 *
 * The easy alternative — `GET /api/grounds/nearest?lat=&lng=` — puts every
 * player's real-time position into the request logs of infrastructure whose
 * retention we don't control. The sensitive fact in this feature has never
 * been where the fields are (public OpenStreetMap data); it is WHO IS WHERE.
 * A "nearest to me" endpoint reintroduces exactly the exposure the #57
 * adversarial review closed, in a worse form, because it is live rather
 * than historical.
 *
 * That rule is enforced by the SHAPE of this module, not by a comment:
 *
 *   1. This file has no network or storage primitives at all — no fetching,
 *      no api client, no browser storage, no logging. `nearMe.test.js`
 *      asserts that against the source text, so adding one fails the suite
 *      rather than the review.
 *   2. `findNearMe` takes the grounds it ranks as an ARGUMENT. The caller
 *      must therefore have finished loading the directory BEFORE this
 *      module ever sees a position — the position cannot be smuggled into
 *      the request that produced the list, because the request is over.
 *   3. The position is returned to the caller and held in component state
 *      only. It dies with the component. Turning it into something durable
 *      (a ground record at those coordinates) takes a SECOND explicit press
 *      — see the "add this place" path in `GroundPicker` / `GroundsMap`,
 *      which the ticket specifies and which is a different consent event
 *      from "rank this list for me".
 *
 * Explicit non-goals, restated because they are easy to drift into: no
 * background or continuous location (this reads a position ONCE, per press,
 * and `watchPosition` appears nowhere), no automatic check-in or attendance
 * detection, no storing where a player was. "Match the player's position to
 * a ground" here means *help them fill a field in faster*.
 */

import { coordinate, nearestGrounds } from "./distance";

/**
 * The stated radius. Beyond this the answer is "nothing near you" plus the
 * offer to add the place — not a ground in the next region ranked first.
 */
export const NEAR_ME_RADIUS_KM = 50;

/** How many nearest grounds a picker dropdown shows. */
export const NEAR_ME_LIMIT = 8;

/**
 * A fix has to arrive while the player is still looking at the field. Ten
 * seconds, then the plain timeout line and back to typing.
 */
export const NEAR_ME_TIMEOUT_MS = 10000;

/**
 * `enableHighAccuracy: false` on purpose — ranking grounds needs a
 * neighbourhood, not a doorstep, and the coarse fix is faster, cheaper on
 * battery, and less invasive. `maximumAge: 0` because a press means "where
 * am I now", and a cached fix from an earlier page is a stale answer nobody
 * asked for.
 */
export const GEOLOCATION_OPTIONS = Object.freeze({
  enableHighAccuracy: false,
  timeout: NEAR_ME_TIMEOUT_MS,
  maximumAge: 0,
});

/**
 * One plain line per failure mode. Each names what happened and points back
 * at the thing that always works — typing — because the form must never
 * block on this.
 */
export const NEAR_ME_MESSAGES = Object.freeze({
  denied: "Location is turned off for this site. Type the ground name instead.",
  unavailable: "Your device couldn't work out where it is. Type the ground name instead.",
  timeout: "Finding your location took too long. Type the ground name instead.",
  unsupported: "This browser can't share your location. Type the ground name instead.",
  insecure: "Location needs a secure (https) connection. Type the ground name instead.",
});

/**
 * The one failure that is ours rather than the device's: the grounds
 * directory didn't load, so there is nothing to rank. Said separately
 * because the remedy is different (retry, not "check your settings") — and
 * because a directory that failed to load must never be reported as "no
 * grounds near you", which would send the player off to add a ground that
 * already exists.
 */
export const DIRECTORY_UNAVAILABLE_MESSAGE =
  "Couldn't load the grounds directory just now. Type the ground name instead.";

/** The button's own label, in one place, so both surfaces say the same thing. */
export const NEAR_ME_LABEL = "Use my location";

/** Shown while the fix is in flight. Text, not a spinner — see the reduced-motion note in the components. */
export const NEAR_ME_BUSY_LABEL = "Finding you…";

/**
 * Map a `GeolocationPositionError` to one of our reasons.
 *
 * The numeric codes are the spec's (1 PERMISSION_DENIED, 2
 * POSITION_UNAVAILABLE, 3 TIMEOUT) and are read as numbers rather than off
 * the error's own constants, because the object a test double hands us has
 * the code and not the class.
 */
export function reasonForPositionError(err) {
  switch (Number(err?.code)) {
    case 1: return "denied";
    case 3: return "timeout";
    case 2: return "unavailable";
    default: return "unavailable";
  }
}

/** A rejected read, in the shape the UI renders: a reason and one plain line. */
export function locationFailure(reason) {
  const key = NEAR_ME_MESSAGES[reason] ? reason : "unavailable";
  return { ok: false, reason: key, message: NEAR_ME_MESSAGES[key] };
}

function defaultGeolocation() {
  if (typeof navigator === "undefined") return null;
  return navigator.geolocation || null;
}

function defaultSecureContext() {
  if (typeof window === "undefined") return true;
  // `isSecureContext` is absent in some test environments; absent is not a
  // failure signal, only an explicit `false` is.
  return window.isSecureContext !== false;
}

/**
 * Read the device position ONCE.
 *
 * Never called on mount, never on focus, never on a render — only from a
 * button's click handler. An unasked-for permission prompt is the fastest
 * way to get location denied permanently, and a denial is forever.
 *
 * Resolves `{ ok: true, position: { lat, lng } }` or a `locationFailure`.
 * It does not throw: every caller here is a click handler on a form that
 * has to stay usable, and an unhandled rejection in one of those is a dead
 * button with no explanation.
 *
 * `geolocation` and `secureContext` are injectable so the failure modes are
 * testable as behaviour rather than as a comment (see `nearMe.test.js`).
 */
export function readDevicePosition({
  geolocation = defaultGeolocation(),
  secureContext = defaultSecureContext(),
  options = GEOLOCATION_OPTIONS,
} = {}) {
  if (!geolocation || typeof geolocation.getCurrentPosition !== "function") {
    return Promise.resolve(locationFailure("unsupported"));
  }
  if (!secureContext) {
    return Promise.resolve(locationFailure("insecure"));
  }
  return new Promise((resolve) => {
    let settled = false;
    const done = (value) => { if (!settled) { settled = true; resolve(value); } };
    try {
      geolocation.getCurrentPosition(
        (pos) => {
          const lat = coordinate(pos?.coords?.latitude, 90);
          const lng = coordinate(pos?.coords?.longitude, 180);
          if (lat === null || lng === null) return done(locationFailure("unavailable"));
          done({ ok: true, position: { lat, lng } });
        },
        (err) => done(locationFailure(reasonForPositionError(err))),
        options,
      );
    } catch {
      // Some browsers throw synchronously in a non-secure context rather
      // than calling the error callback.
      done(locationFailure("unavailable"));
    }
  });
}

/**
 * The whole "Use my location" pipeline, in one testable function.
 *
 * `grounds` is the directory the CALLER has already loaded — this module
 * never requests it, which is what makes "nothing carries the position"
 * structural rather than aspirational (see point 2 in the module doc).
 *
 * Returns either
 *   `{ ok: true, position, results }` — `results` nearest-first with
 *      `_km` and `_distanceLabel` attached; EMPTY when nothing is within
 *      `maxKm`, which is the caller's cue to offer "add this place"
 *      (position included, for the one explicit press that needs it), or
 *   `{ ok: false, reason, message }` — one plain line, form still usable.
 */
export async function findNearMe(grounds, {
  limit = NEAR_ME_LIMIT,
  maxKm = NEAR_ME_RADIUS_KM,
  ...readOptions
} = {}) {
  const read = await readDevicePosition(readOptions);
  if (!read.ok) return read;
  return {
    ok: true,
    position: read.position,
    results: nearestGrounds(read.position, grounds, { limit, maxKm }),
  };
}
